import { useState, useEffect, useCallback } from 'react'
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react'
import PageHeader from '@/components/feature/PageHeader'
import { gallery, type GallerySpan } from '@/data/gallery'
import { useLanguage } from '@/localization'

// Full literal class strings so Tailwind's content scanner keeps them.
const spanClass: Record<GallerySpan | 'normal', string> = {
  big: 'col-span-2 row-span-2',
  wide: 'col-span-2 row-span-1',
  tall: 'row-span-2',
  normal: '',
}

export default function GalleryPage() {
  const [index, setIndex] = useState<number | null>(null)
  const active = index === null ? null : gallery[index]
  const { t } = useLanguage()

  const close = useCallback(() => setIndex(null), [])
  const step = useCallback(
    (dir: number) =>
      setIndex((i) => (i === null ? i : (i + dir + gallery.length) % gallery.length)),
    [],
  )

  useEffect(() => {
    if (index === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') step(1)
      else if (e.key === 'ArrowLeft') step(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, close, step])

  return (
    <>
      <PageHeader
        title="Gallery"
        subtitle="Real shots from the Uliunai survival world — villages, builds, boss fights and the road in between, shaders on."
      />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[150px] md:auto-rows-[185px] gap-3 [grid-auto-flow:dense]">
          {gallery.map((item, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`group relative overflow-hidden rounded-2xl border border-stone-light/50 shadow-block bg-slate-deep/40 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-grass ${spanClass[item.span ?? 'normal']}`}
            >
              <img
                src={item.src}
                alt={t(item.caption)}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.08]"
              />
              {/* darken on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-deep via-slate-deep/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {/* zoom hint */}
              <span className="absolute top-3 right-3 w-9 h-9 rounded-lg bg-black/40 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Maximize2 size={16} />
              </span>
              <div className="absolute inset-x-0 bottom-0 p-4 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <p className="text-white font-bold leading-tight drop-shadow">{t(item.caption)}</p>
                {item.credit && <p className="text-white/60 text-xs mt-0.5">{t(item.credit)}</p>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-slate-deep/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
          onClick={close}
        >
          <button
            className="absolute top-5 right-5 w-11 h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer z-10"
            onClick={close}
            aria-label={t('Close')}
          >
            <X size={24} />
          </button>

          <button
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer z-10"
            onClick={(e) => {
              e.stopPropagation()
              step(-1)
            }}
            aria-label={t('Previous')}
          >
            <ChevronLeft size={26} />
          </button>
          <button
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer z-10"
            onClick={(e) => {
              e.stopPropagation()
              step(1)
            }}
            aria-label={t('Next')}
          >
            <ChevronRight size={26} />
          </button>

          <figure
            className="w-full max-w-[1600px] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.src}
              alt={t(active.caption)}
              className="w-full max-h-[86vh] object-contain rounded-2xl shadow-block-lg"
            />
            <figcaption className="text-center text-white mt-4">
              <span className="font-bold">{t(active.caption)}</span>
              {active.credit && <span className="text-white/50 text-sm"> · {t(active.credit)}</span>}
              <span className="block text-white/40 text-xs mt-1">
                {(index ?? 0) + 1} / {gallery.length}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </>
  )
}
